/**
 * B1's calls. Every one goes through `../../api/client.js` and nothing else.
 *
 * ── WHAT B1 IS MADE OF, AND WHICH ROUTE OWNS EACH PART ───────────────────────
 * The home screen was drawn as one picture and it is assembled from three facts, each
 * belonging to a route that already exists:
 *
 *   the person's name      `GET /v1/account/profile`     → `profile.name_bn`
 *   the live order         `GET /v1/activity?tab=active` → the first row, then
 *                          `GET /v1/activity/:id`        → `status` (title, detail, percent)
 *   the repeat cards       `GET /v1/home/repeats`        → THE SLOT, see below
 *
 * There is deliberately no `GET /v1/home`. A screen-shaped endpoint would have to
 * duplicate three payloads that already exist and would then be free to disagree with
 * them — the account home claiming one name while the profile screen shows another is
 * the class of bug `account/index.ts § why F1, F2 and F5 are one read` exists to prevent,
 * and inventing a fourth source of the same three facts would reintroduce it on the
 * busiest screen in the product.
 *
 * ── WHY THE LIVE ORDER TAKES TWO READS ───────────────────────────────────────
 * B1 draws a PROGRESS BAR — `width:60%` on a teal fill, with the technician's name and
 * an estimate beside it. `ActivityListRow` has no percent and no status card; that lives
 * on `ActivityDetail.status` (`ActivityStatusCard`: `title_bn`, `time_bn`, `percent`,
 * `detail_bn`), which is E2's read. So the list read says WHICH order is live and the
 * detail read says what to draw about it. The alternative — a percent added to the list
 * row — is an API change, and inventing the number on the client is the thing that put
 * `width:60%` in a constant in the first place.
 *
 * ── THE TAB IS A QUERY, NOT A FILTER ─────────────────────────────────────────
 * `tab=active` is sent to the server and the server decides which status keys are চলমান
 * (`apps/api/src/activity/copy.ts § DONE_STATUS_KEYS`). A client-side
 * `status_key === 'in_progress'` would put that decision in a transcription, and a status
 * nobody has thought about yet would vanish from the screen instead of landing where the
 * customer can see it. E1 makes the same argument about the same query.
 *
 * ── `GET /v1/home/repeats` IS A SLOT, AND IT IS EMPTY TODAY ──────────────────
 * `আবার প্রয়োজন হতে পারে` is a SUGGESTION surface, not a history list: B1 draws
 * `বিদ্যুৎ বিল · DPDC · গত মাস`, where `গত মাস` is a relative phrase and `DPDC` is the
 * biller — a line no activity row carries. E1's own row for the same payment reads
 * `DPDC · ২৪ আগস্ট`, because it is composed for a dated list. Rendering E1's line on B1's
 * card would put one screen's phrasing on another's card, and splitting `meta_bn` on its
 * `·` to swap half of it is the client composing Bangla, which docs/05 § conventions
 * forbids in as many words.
 *
 * So the card reads a route of its own, the route does not exist yet, and the gap is
 * NAMED rather than papered over: `docs/bangla-needed/home-search.md § 1` records the
 * shape the Worker has to answer with, and the gate serves it from
 * `fixtures/api/home.b1-repeats.json`, whose every string is transcribed from frame B1.
 * A missing route leaves the two cards unrendered and says so in the console; it does not
 * take the name and the live order down with it, and it does not fall back to drawn data.
 *
 * ── NOTHING HERE FALLS BACK TO DRAWN DATA ────────────────────────────────────
 * No fixture behind the call, no "if the API is unreachable, show the frame's values".
 * A screen that silently substitutes design data for live data looks correct in review
 * and lies in production — which is the defect this whole pass exists to end. When a read
 * fails the screen renders less, or the boundary renders a drawn STATE, which is a
 * different thing: it says something true about the failure.
 */
import type {
  AccountOverview,
  ActivityDetail,
  ActivityListResponse,
  ActivityStatusCard,
} from '@oprappo/contracts';
import { ApiError, UnmappedApiError, api } from '../../api/client.js';

/** How many repeat cards B1 draws. Two, side by side, each `flex-1`. */
export const REPEAT_CARD_COUNT = 2;

/**
 * What the home screen renders. Every field is nullable and the screen draws less when
 * one is missing — a signed-out visitor has no name and no orders, and that is a
 * coherent screen rather than a failure.
 */
export interface HomeData {
  /** `profile.name_bn`. Null for a visitor with no account, never a placeholder. */
  name_bn: string | null;
  /** The live order's id, so the card can open E2. */
  live_id: string | null;
  /** E2's status card: the title, the detail line and the percent B1 draws. */
  live: ActivityStatusCard | null;
  /** The two cards under `আবার প্রয়োজন হতে পারে`, in the server's order. */
  repeats: HomeRepeat[];
}

/**
 * One `আবার প্রয়োজন হতে পারে` card, as B1 draws it: a title, a detail line and an amount.
 *
 * ── WHY THIS SHAPE IS DECLARED HERE AND NOT IN `@oprappo/contracts` ──────────
 * `packages/contracts` is append-only during a wave and is the shared vocabulary between
 * a Worker route and this client. There is no Worker route yet, so there is nothing to
 * share: putting the shape there now would publish a contract neither side implements.
 * It moves to contracts in the same change that mounts `GET /v1/home/repeats`, which is
 * the change that makes it a contract rather than a wish.
 *
 * `detail_bn` is ALREADY LOCALISED, like every other user-facing string in a response
 * body (docs/05 § conventions). `updated_at` travels beside it as RFC 3339 so the card
 * can say how long ago this was WITHOUT the client writing the sentence — see
 * `apps/web/src/lib/relative-time.ts`, whose whole vocabulary is six drawn phrases and
 * which returns null rather than inventing a seventh.
 */
export interface HomeRepeat {
  id: string;
  title_bn: string;
  /** B1's second line, e.g. `DPDC · গত মাস`. Server-composed; the client composes none. */
  detail_bn: string;
  /** Integer minor units (paisa), per docs/05 § conventions. */
  amount_minor: number;
  /** RFC 3339. Null where the suggestion is not derived from a dated event. */
  updated_at: string | null;
  /** Where the card goes. Null until the destination exists, and then it is the server's. */
  to: string | null;
}

export interface HomeRepeatsResponse {
  items: HomeRepeat[];
}

/**
 * A refusal that is not a failure of this screen.
 *
 * A visitor with no account gets `E_PERMISSION` from the three reads below, and B1 must
 * still render: search is reachable before an account exists (docs/05 § search), and the
 * screen in front of it is the first thing anybody sees. So a permission refusal on the
 * home screen resolves to "nothing to show for this person" rather than being raised to
 * the boundary, where it would map to `অনুমতি নেই` — a state drawn about a ফাউন্ডার-mode
 * role reading a cap table, which nobody is doing on the home screen.
 *
 * Every OTHER failure is re-thrown and reaches the drawn state it belongs to. An outage
 * still shows ইন্টারনেট নেই; only "you are not signed in" is absorbed, and only here.
 */
function emptyOnRefusal<T>(fallback: T): (cause: unknown) => T {
  return (cause: unknown): T => {
    if (cause instanceof ApiError && cause.code === 'E_PERMISSION') return fallback;
    throw cause;
  };
}

/**
 * One load of B1.
 *
 * The three reads run together rather than in sequence, because none of them needs
 * another's answer — except the live order's detail, which needs the list's first row and
 * is chained behind it. `Promise.all` over the two independent halves keeps the screen's
 * time-to-paint at one round trip plus one, not three.
 */
export async function fetchHome(signal?: AbortSignal): Promise<HomeData> {
  const [name_bn, live, repeats] = await Promise.all([
    fetchName(signal),
    fetchLiveOrder(signal),
    fetchRepeats(signal),
  ]);
  return { name_bn, live_id: live.id, live: live.status, repeats };
}

async function fetchName(signal?: AbortSignal): Promise<string | null> {
  return api
    .get<AccountOverview>('/v1/account/profile', { signal })
    .then((account) => account.profile.name_bn)
    .catch(emptyOnRefusal<string | null>(null));
}

async function fetchLiveOrder(
  signal?: AbortSignal,
): Promise<{ id: string | null; status: ActivityStatusCard | null }> {
  const list = await api
    .get<ActivityListResponse>('/v1/activity?tab=active', { signal })
    .catch(emptyOnRefusal<ActivityListResponse | null>(null));
  // B1 draws ONE live order. The server returns the active list in its own order and the
  // first row is the one the screen shows; a client that picked "the most urgent" would
  // be ranking orders in a transcription.
  const first = list?.items[0];
  if (first === undefined) return { id: null, status: null };

  const detail = await api
    .get<ActivityDetail>(`/v1/activity/${encodeURIComponent(first.id)}`, { signal })
    .catch(emptyOnRefusal<ActivityDetail | null>(null));
  if (detail === null) return { id: null, status: null };
  return { id: detail.id, status: detail.status };
}

/**
 * The route the repeat cards read. Not built; see the file header.
 *
 * One name, in one place, so the fixture, the test and the call site cannot drift.
 */
export const REPEATS_PATH = '/v1/home/repeats';

async function fetchRepeats(signal?: AbortSignal): Promise<HomeRepeat[]> {
  return api
    .get<HomeRepeatsResponse>(REPEATS_PATH, { signal })
    .then((body) => body.items.slice(0, REPEAT_CARD_COUNT))
    .catch((cause: unknown) => {
      if (cause instanceof ApiError && cause.code === 'E_PERMISSION') return [];
      // The route is not mounted yet, so a Worker that has never heard of it answers
      // something this client has no designed state for. That is exactly what
      // `UnmappedApiError` is: a gap in what the backend offers, meant to be noticed.
      // It is written out with the path so the console names the missing route rather
      // than the screen, and the rest of B1 still renders.
      if (cause instanceof UnmappedApiError) {
        console.warn(`GET ${REPEATS_PATH} is not served yet; B1 renders no repeat cards`, cause);
        return [];
      }
      throw cause;
    });
}
